// public/v3/src/lib/decision-inbox.js — pure enumeration of pending operator
// decisions for the Studio "Decisions" inbox.
//
// CJS (Jest-loadable) like ccm-tier-bump.js; studio.jsx imports via ESM
// interop. Pure functions only — no React, no fetches, and it never WRITES a
// decision: items describe what is awaiting the operator, the actions live in
// the existing review modals (TierBumpReviewModal, BhiNoAssessmentReviewModal,
// PainNoScaleReviewModal) so the inbox can never grow a second write path.
//
// Resolution semantics per kind (all v1 kinds are non-blocking decisions —
// none of them hard-blocks billing, and the copy must never claim they do):
//   'tier'     — Standard-CCM month at >= 90 CCM minutes with NO
//                ccmTierMonth[month] affirmation. Resolves when any tier is
//                affirmed (a stored affirmation of any code = decided) or the
//                month is billed. "Keep 99490" stores nothing by design
//                (writeCcmTierAffirmation deletes on empty affirm), so a
//                kept-standard month stays enumerable until billed — callers
//                may resolve it session-locally.
//   'bhi-ack'  — BHI bill blocked SOLELY by the missing initial assessment
//                (soft gate; override-able at generate/push). Resolves when an
//                assessment is recorded or the month is billed.
//   'pain-ack' — G3002 blocked SOLELY by the missing in-month validated scale
//                (soft gate). Resolves when a scale is recorded or the month
//                is billed.

const { isTierBumpCandidate, physicianTimePct } = require('./ccm-tier-bump');
const { resolveBhiBillCode, bhiGatesForCode, entryProgramFamily } = require('./bhi-billing');
const { isPainEnrolledPatient, painGatesForMonth } = require('./pain-billing');

function _raw(patient) {
  return patient && patient._raw ? patient._raw : (patient || {});
}

// CCM-family minutes for the month. Mirrors push-emr.js ccmMonthlyMinutes
// (ESM, not require-able from CJS) — the two must stay in sync; both mirror
// the summing loop in ccm-tier-bump.js physicianTimePct.
function ccmFamilyMinutes(raw, monthStr) {
  const p = _raw(raw);
  const log = Array.isArray(p.activityLog) ? p.activityLog : [];
  let total = 0;
  for (const e of log) {
    if (!e || e.isConsolidatedNote) continue;
    if (String(e.date || '').indexOf(monthStr) !== 0) continue;
    if (entryProgramFamily(e) !== 'ccm') continue;
    total += parseInt(e.duration, 10) || 0;
  }
  return total;
}

// True when a BHI action for the month is blocked SOLELY by the missing
// initial assessment. Port of studio.jsx bhiNeedsAssessmentOverride — studio
// delegates here so the inbox and the generate-time gate can never disagree.
function pendingBhiAck(raw, monthStr) {
  const p = _raw(raw);
  const code = resolveBhiBillCode(p, monthStr);
  // Override is 99484-only: G0570 (initiation visit) requires the initial
  // assessment as part of the visit itself — never soft-guard past it.
  if (code === 'G0570') return false;
  const strict = bhiGatesForCode(p, monthStr, code);
  if (strict.ok) return false;
  const withOverride = bhiGatesForCode(p, monthStr, code, { assessmentOverride: true });
  return withOverride.ok === true
    && (withOverride.warnings || []).includes('Initial assessment missing');
}

// True when G3002 is blocked SOLELY by the missing in-month NRS. Port of the
// predicate core of studio.jsx painNeedsScaleAck WITHOUT the role check —
// role visibility (canManagePain) is the caller's concern.
function pendingPainAck(raw, monthStr, staffList) {
  const p = _raw(raw);
  if (!isPainEnrolledPatient(p)) return false;
  const strict = painGatesForMonth(p, monthStr, staffList);
  if (strict.ok) return false;
  const withOverride = painGatesForMonth(p, monthStr, staffList, { scaleOverride: true });
  return withOverride.ok === true
    && (withOverride.warnings || []).includes('No validated pain scale administered this month (acknowledged by operator)');
}

function _round2(n) {
  return Math.round(n * 100) / 100;
}

// Main entry point: enumerate pending decisions over the roster.
//   ctx.staffList      — staff array for credential lookups.
//   ctx.rankTiers      — OPTIONAL (minutes) => [{ code, dollars }] high→low.
//                        Injected because the real ranking lives in
//                        billing-generate.js (ESM — this lib must stay CJS);
//                        studio passes rankCcmTiersByReimbursement. Absent →
//                        dollars/dollarDelta/topCode are null, items still
//                        enumerate.
//   ctx.isBilled       — OPTIONAL (patientId, kind) => bool; a billed month
//                        has no pending decision of that kind. Called PER KIND
//                        because the legs differ: 'tier'/'pain-ack' ride the
//                        CCM bill, 'bhi-ack' rides the BHI leg (bhi_bn slot) —
//                        a CCM-billed month must not hide a pending BHI
//                        decision (see push-emr.js getWorkflowState).
//   ctx.canManagePain  — pain ack items enumerate only when true (mirrors the
//                        studio provider/admin gate on pain surfaces).
// Ordering: blocking items first (none in v1 — the field exists so future
// hard-deadline kinds slot in without reshuffling consumers), then tier items
// by dollarDelta desc (nulls last, ties by name), then acks by name. Stable.
function pendingDecisions(patients, monthStr, ctx) {
  const list = Array.isArray(patients) ? patients : [];
  const c = ctx || {};
  const staffList = Array.isArray(c.staffList) ? c.staffList : [];
  const billed = typeof c.isBilled === 'function' ? c.isBilled : () => false;
  const items = [];
  for (const patient of list) {
    if (!patient) continue;
    const raw = _raw(patient);
    const patientId = patient.id || raw.id;
    const name = patient.name || raw.name || '';

    const minutes = ccmFamilyMinutes(raw, monthStr);
    const affirmed = !!(raw.ccmTierMonth && raw.ccmTierMonth[monthStr]);
    if (!affirmed && !billed(patientId, 'tier') && isTierBumpCandidate(raw, monthStr, minutes)) {
      const hint = physicianTimePct(raw, monthStr, staffList);
      let dollars = null;
      let dollarDelta = null;
      let topCode = null;
      if (typeof c.rankTiers === 'function') {
        const ranked = c.rankTiers(minutes) || [];
        if (ranked.length) {
          topCode = ranked[0].code;
          dollars = _round2(ranked[0].dollars);
          const keep = ranked.find((r) => r.code === '99490');
          dollarDelta = keep ? _round2(ranked[0].dollars - keep.dollars) : null;
        }
      }
      items.push({
        kind: 'tier', patientId, name, minutes,
        physicianPct: hint.pct, providerMin: hint.providerMin,
        blocking: false, dollars, dollarDelta, topCode,
      });
    }

    if (!billed(patientId, 'bhi-ack') && pendingBhiAck(raw, monthStr)) {
      items.push({ kind: 'bhi-ack', patientId, name, blocking: false });
    }

    if (c.canManagePain === true && !billed(patientId, 'pain-ack')
        && pendingPainAck(raw, monthStr, staffList)) {
      items.push({ kind: 'pain-ack', patientId, name, blocking: false });
    }
  }

  const kindRank = (it) => (it.kind === 'tier' ? 0 : 1);
  items.sort((a, b) => {
    if (a.blocking !== b.blocking) return a.blocking ? -1 : 1;
    if (kindRank(a) !== kindRank(b)) return kindRank(a) - kindRank(b);
    if (kindRank(a) === 0) {
      const ad = a.dollarDelta == null ? -Infinity : a.dollarDelta;
      const bd = b.dollarDelta == null ? -Infinity : b.dollarDelta;
      if (ad !== bd) return bd - ad;
    }
    return String(a.name).localeCompare(String(b.name));
  });
  return items;
}

function decisionCounts(items) {
  const list = Array.isArray(items) ? items : [];
  let tier = 0;
  let acks = 0;
  for (const it of list) {
    if (it.kind === 'tier') tier += 1;
    else acks += 1;
  }
  return { total: list.length, tier, acks };
}

module.exports = {
  ccmFamilyMinutes,
  pendingBhiAck,
  pendingPainAck,
  pendingDecisions,
  decisionCounts,
};
